import { TypedDocumentNode, gql } from '@apollo/client';
import { addDays, isToday, parse, startOfDay } from 'date-fns';
import { useState } from 'react';

import { FootballRivalsCompetitionSortType } from '@sorare/core/src/__generated__/globalTypes';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useLocalStorage, {
  STORAGE,
} from '@sorare/core/src/hooks/useLocalStorage';
import { formatDateForUrl } from '@sorare/core/src/lib/routing';

import { DrawerOrDialog } from 'components/rivals/DrawerOrDialog';
import { GameSelection } from 'components/rivals/GameSelection';

import { RivalryGame } from './RivalryGame';
import {
  RivalryGamesSelectionQuery,
  RivalryGamesSelectionQueryVariables,
} from './__generated__/index.graphql';

const RIVALRY_GAMES_BY_LEAGUE_QUERY = gql`
  query RivalryGamesSelectionQuery(
    $userSlug: String!
    $fromDate: ISO8601DateTime!
    $toDate: ISO8601DateTime!
    $sortBy: FootballRivalsCompetitionSortType
  ) {
    football {
      rivals {
        id
        manager(userSlug: $userSlug) {
          id
          myRivalry {
            id
            upcomingGamesByLeague(
              fromDate: $fromDate
              toDate: $toDate
              sortBy: $sortBy
            ) {
              games {
                slug
                ...RivalryGame_footballRivalsGame
              }
              ...GameSelection_footballRivalsLeagueGame
            }
          }
          ...RivalryGame_footballRivalsManager
        }
      }
    }
  }
  ${GameSelection.fragments.footballRivalsLeagueGame}
  ${RivalryGame.fragments.footballRivalsGame}
  ${RivalryGame.fragments.footballRivalsManager}
` as TypedDocumentNode<
  RivalryGamesSelectionQuery,
  RivalryGamesSelectionQueryVariables
>;

type Props = {
  opponentUserSlug: string;
  open: boolean;
  onClose: () => void;
  selectedDate?: string | null;
};
export const RivalryGamesSelection = ({
  opponentUserSlug,
  open,
  onClose,
  selectedDate,
}: Props) => {
  const initialDate = selectedDate
    ? parse(selectedDate, 'yyyy-MM-dd', new Date())
    : new Date();
  const [fromDate, setFromDate] = useState(
    isToday(initialDate) ? initialDate : startOfDay(initialDate)
  );
  const [toDate, setToDate] = useState(startOfDay(addDays(initialDate, 1)));

  const [sortBy, setSortBy] = useLocalStorage(
    STORAGE.rivalsSortBy,
    FootballRivalsCompetitionSortType.FAVORITE_COMPETITION
  );
  const onSortChange = (sorting: FootballRivalsCompetitionSortType) => {
    setSortBy(sorting);
  };

  const { data, loading, refetch } = useQuery(RIVALRY_GAMES_BY_LEAGUE_QUERY, {
    variables: {
      userSlug: opponentUserSlug,
      fromDate,
      toDate,
      sortBy,
    },
  });
  const opponent = data?.football.rivals.manager;
  const upcomingGamesByLeague = opponent?.myRivalry?.upcomingGamesByLeague;

  return (
    <DrawerOrDialog open={open} onClose={onClose}>
      <GameSelection
        gamesByLeague={upcomingGamesByLeague}
        renderGame={game =>
          opponent && (
            <RivalryGame
              key={game.slug}
              footballRivalsGame={game}
              footballRivalsManager={opponent}
              selectedDate={formatDateForUrl(fromDate)}
              onCreateInvite={refetch}
            />
          )
        }
        loading={loading}
        fromDate={fromDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        sortBy={sortBy}
        onSortChange={onSortChange}
      />
    </DrawerOrDialog>
  );
};
